export interface UserProfile {
  roles: Record<string, string[]>;
}

export interface Feature {
  name: string;
  img: string;
  role: string;
  path: string;
  description: string;
}

export interface Location {
  latitude: number;
  longitude: number;
}

export interface DateValue {
  date: number;
}

export enum SoilingRiskAssessment {
  LOW = 'LOW',
  HIGH = 'HIGH',
  UNDEFINED = '-',
}

export const colors = ['#2caffe', '#00e272', '#fe6a35', '#6b8abc', '#d568fb', '#2ee0ca', '#fa4b42', '#feb56a', '#91e8e1'];

export const plotLines = {
  soiling_ratio: {
    label: 'Soiling Ratio',
    name: 'Soiling Ratio (incl. human cleaning)',
    color: 'rgb(84, 79, 197)',
  },
  soiling_mass_rate: {
    label: 'Soiling Mass Rate',
    name: 'Soiling Mass Rate',
    color: 'rgb(0, 226, 114)',
  },
  rain: {
    label: 'Precipitation [mm/day]',
    name: 'Precipitation [mm/day]',
    color: 'rgb(69, 200, 255)',
  },
  value: {
    label: 'Value',
    color: 'rgb(69, 200, 255)',
  },
  dc_no_soiling: {
    color: 'rgb(213, 104, 251)',
  },
  ac_soiling_clipping: {
    color: 'rgb(69, 200, 255)',
  },
  ac_soiling: {
    color: 'rgb(69, 200, 255)',
  },
  ac_no_soiling_clipping: {
    color: '#FF6B6B',
  },
  ac_no_soiling: {
    color: '#FF6B6B',
  },
  ac_soiling_no_clipping: {
    color: '#4ECDC4',
  },
  rain_threshold: {
    label: 'Rain Threshold',
    color: 'black',
  },
  market_price: {
    label: 'Market Price',
    color: 'red',
  },
  production: {
    label: 'Production with Soiling', // 'Production',
    color: 'green',
  },
  no_soiling_production: {
    label: 'Production without Soiling', // 'Reference Production',
    color: 'rgb(213, 104, 251)',
  },
  production_losses: {
    label: 'Production Losses',
    color: 'red',
  },
  production_losses_cost: {
    label: 'Production Losses Cost',
    color: 'red',
  },
  cleanings_cost: {
    label: 'Cleanings Cost',
    color: 'blue',
  },
  net_present_value: {
    label: 'Net Present Value',
    color: 'green',
  },
  cleaning: {
    label: 'Cleaning',
    color: 'rgba(255, 165, 0, 0.5)',
  },
};

export const textLabels = {
  ac_capacity: 'AC Capacity (MWac)',
  dc_capacity: 'DC Capacity (MWp)',
  row_length: 'Row Length (m)',
  number_of_rows: 'Number of Rows',
  modules_per_tables: 'Modules per Tables',
  width: 'Width (m)',
  height: 'Height (m)',
  area: 'Area (m²)',
  rows: 'Rows',
  trackers_per_row: 'Trackers per Row',
  tracker_width: 'Tracker Width (m)',
  tables_per_tracker: 'Tables per Tracker',
  modules_per_table: 'Modules per Table',
  not_cleaning: 'Not Cleaning',
  water_refill: 'Water Refill',
  fuel_refill: 'Fuel Refill',
  effective_cleaning: 'Effective Cleaning',
  maintenance: 'Maintenance',
  water: 'Water',
  fuel: 'Fuel',
  workers: 'Workers',
  bridges: 'Bridges',
  device: 'Device',
  brush: 'Brush',
  transition: 'Transition',
  battery: 'Battery',
  motor: 'Motor',
  yearly: 'Yearly',
  cleaning_opex: 'Cleaning Opex',
  capex: 'Capex',
  ac_soiling_no_clipping: 'AC production with soiling losses and without clipping losses',
  ac_soiling_clipping: 'AC production with soiling and clipping losses',
  ac_no_soiling_clipping: 'AC production without soiling losses and with clipping losses',
  dc_no_soiling: 'DC production without soiling losses',
  ac_soiling: 'AC production with soiling losses',
  ac_no_soiling: 'AC production without soiling losses',
};
export interface Project {
  id: string;
  name: string;
  description: string;
  progress: number;
  soiling_risk_assessment: SoilingRiskAssessment;
  monthly_soiling_rate: number | null;
  cleaning_solution: string | null;
  yearly_cleaning_frequency: number | null;
  location: {
    longitude: number;
    latitude: number;
  };
}

export type ChildrenProps = {
  children: React.ReactNode;
};

export interface Node {
  _id: string;
  account_id?: string;
  node_name: string;
  path: string;
  id_path: string;
  type?: string;
  open?: boolean;
  nodes?: Node[];
}

export interface Account {
  _id: string;
  account_id: string;
  node_name: string;
  open: boolean;
  favorite?: boolean;
  type?: string;
  nodes?: Node[];
  path?: string;
}

export interface Params {
  url: string;
}

export interface AppError {
  title: string;
  message: string;
  active: boolean;
}

export interface Path {
  id: string;
  name: string;
}

export interface Context {
  loading?: boolean;
  error?: AppError;
  path?: Path[];
  path_names?: string;
  node_id?: string;
  param?: string;
  auth?: any;
  support?: boolean;
  test?: string;
}

export interface TableData {
  link_to_node: string;
  asset_name: string;
}

export interface DashboardTab {
  dashboard_id: number;
  heading: string;
}

export interface Flag {
  label: string;
  color: string;
  description: string;
  value: number;
}

export interface Alert {
  alert_description: string;
  level_color: string;
  level_name: string;
  level_type: string;
  level_value: number;
}

export interface DetailPanelItem {
  data_name: string;
  label: string;
}

export interface OverviewColumn {
  data_name: string;
  description: string;
  flags: any;
  label: string;
  type: string;
}

export interface UserNodeSection {
  column_pinning: string[];
  dashboard_tabs: DashboardTab[];
  detail_panel: DetailPanelItem[];
  order_by: string[];
  overview_columns: OverviewColumn[];
  overview_data: any[];
}
export interface DashboardData {
  dashboard_tabs: DashboardTab[];
  id: string;
  node_components: { modules: string[] };
  node_description: string | null;
  node_name: string;
  node_type: string;
  user_node_sections: UserNodeSection[];
  section_order: number;
  section_title: string;
  section_type: string;
}

export interface Timeserie {
  device_id: string;
  timeserie_name: string;
  include_alert_levels?: boolean;
  timeserie_alert_level_name?: string;
}

export interface TimeseriesQuery {
  begin: number;
  end: number;
  timeseries: Timeserie[];
}

export interface TimeseriePoints {
  data_range_x: number[];
  data_range_y: number[];
  data_values: number[][];
  device_id: string;
  device_name: string;
  is_alert_level: boolean;
  timeserie_label: string;
  timeserie_name: string;
  timeserie_unit: string;
  timeserie_alert_level_name: string;
  aggregated: boolean;
}

export interface Report {
  asset: string;
  fleet: string;
  report: string;
  upload_time: string;
}

export interface Trace {
  trace_color: string;
  trace_label: string;
  trace_mode: string;
  x_values: number[];
  y_values: number[];
}

export interface AssetXYChart {
  static_traces: Trace[];
  timeserie_traces: any[];
  x_axis_label: string;
  y_axis_label: string;
}
export interface SectionData {
  asset_xy_chart?: AssetXYChart;
  overview_data?: any;
  overview_columns?: any;
  is_online: boolean;
}

export interface ChartTrace {
  x?: number[];
  y?: number[];
  t?: number[];
  line?: any;
  type?: string;
  marker?: any;
  mode?: string;
  name?: string;
  hoverinfo?: string;
  text?: string;
  showlegend?: boolean;
}

export interface ChartLayout {
  height?: number;
  width?: number;
  margin?: any;
  xaxis?: any;
  yaxis?: any;
  showlegend?: boolean;
  legend?: any;
}

export interface Solution {
  num_devices: number;
  num_cleanings: number;
  net_present_value: number;
  cashflow?: {
    cleaning_gains: number[];
    capex: number[];
    opex: number[];
  };
  data: {
    daily: {
      values: {
        soiling_ratio?: number[] | undefined;
        production?: number[];
      };
      units: {
        soiling_ratio: string;
        production?: number[];
      };
    };
    monthly: {
      values: {
        soiling_ratio?: number[] | undefined;
        production?: number[] | undefined;
      };
      units: {
        soiling_ratio: string;
        production?: number[];
      };
    };
    yearly: {
      values: {
        soiling_ratio?: number[];
        production?: number[];
      };
      units: {
        soiling_ratio: string;
        production?: number[];
      };
    };
  };
  schedule: string[];
}

export interface SolutionPackage {
  name: string;
  optimal_index: number;
  solutions: Solution[];
}
